<template>
  <!-- The js-lib Dialog panel is a plain rounded card; the AI-mode intro wants a wider, softer one
       that fills most of the viewport, hence the panel overrides and the `flex-1` body. -->
  <Dialog
    :title="t('AI-mode')"
    headless
    class="w-full max-w-3xl rounded-2xl! md:min-h-[40vh] text-sm"
    body-class="flex-1 flex flex-col gap-10 md:gap-16 overflow-y-auto p-6 md:p-8 text-foreground"
    @close="emit('close')">
    <!-- Brand row -->
    <div class="flex-none flex items-start justify-between gap-4">
      <div class="flex items-center gap-2">
        <AiModeIcon class="size-5 flex-none" />
        <span class="text-lg font-medium">{{ t("AI-mode") }}</span>
        <Pill variant="muted">{{ t("Beta") }}</Pill>
      </div>
      <button
        type="button"
        :aria-label="t('Close')"
        class="flex-none flex size-9 items-center justify-center rounded-lg text-foreground hover:bg-accent transition"
        @click="emit('close')">
        <CloseIcon class="size-6" />
      </button>
    </div>

    <div class="flex-none flex flex-col gap-6 px-24 py-22">
      <h2 class="text-4xl md:text-5xl font-medium tracking-tight">{{ t("Welcome at AI-mode") }}</h2>

      <div class="flex flex-col gap-8">
        <div class="flex flex-col gap-1">
          <h3 class="font-bold">{{ t("What you can do") }}</h3>
          <ul class="list-disc ps-6 space-y-1">
            <li>{{ t("Search for events/tickets in your own words") }}</li>
            <li>{{ t("Compare tickets directly") }}</li>
            <li>{{ t("Get personalized recommendations") }}</li>
          </ul>
        </div>

        <div class="flex flex-col gap-1">
          <h3 class="font-bold">{{ t("Good to know") }}</h3>
          <p>
            {{
              t(
                "AI answers are helpful, but not perfect. Always check event details and specifications. You can switch back to the classic search function at any time."
              )
            }}
          </p>
        </div>

        <div class="flex flex-col gap-2">
          <h3 class="font-bold">{{ t("Search suggestions") }}</h3>
          <div class="flex flex-wrap gap-2">
            <button
              v-for="suggestion in suggestions"
              :key="suggestion"
              type="button"
              class="rounded-full bg-accent px-4 py-1.5 hover:bg-surface-focus transition"
              @click="applySuggestion(suggestion)">
              &ldquo;{{ t(suggestion) }}&rdquo;
            </button>
          </div>
        </div>

        <!-- Prompt bar. There is no AI search backend yet, so it only hands the prompt to the parent. -->
        <form
          class="flex items-center gap-2 h-12 px-4 rounded-xl border-[0.5px] border-input bg-accent focus-within:border-ai transition"
          @submit.prevent="submit">
          <AiModeIcon class="size-5 flex-none" />
          <input
            ref="promptElement"
            v-model="prompt"
            name="ai-prompt"
            type="text"
            maxlength="200"
            autocomplete="off"
            :aria-label="t('Ask anything')"
            :placeholder="t('Ask anything')"
            class="flex-1 min-w-0 text-base placeholder-muted-foreground bg-transparent outline-none border-none ring-0 focus:ring-0 focus:outline-none appearance-none" />
        </form>
      </div>
    </div>

    <!-- mt-auto, not a gap: it pins the footer to the bottom of a panel taller than its content. -->
    <p class="flex-none mt-auto text-center text-sm text-muted-foreground">
      {{ t("* AI-based search results. This is not a replacement for support.") }}
      <RouterLink :to="brandconfig.PRIVACY" class="underline">{{ t("Privacy") }}</RouterLink>
      |
      <a :href="brandconfig.GENERAL_TERMS_LINK" target="_blank" rel="noopener" class="underline">
        {{ t("General Terms") }}
      </a>
    </p>
  </Dialog>
</template>

<script setup lang="ts">
import { nextTick, ref, useTemplateRef } from "vue";
import { useI18n } from "vue-i18n";
import { RouterLink } from "vue-router";
import { Dialog, Pill } from "@xs2event/js-lib/components";
import brandconfig from "@/brandconfig";
import AiModeIcon from "@/components/shared/AiModeIcon.vue";
import CloseIcon from "~icons/ic/round-close";

const { t } = useI18n();

const emit = defineEmits<{ close: []; submit: [prompt: string] }>();

const suggestions = [
  "4 tickets under €300",
  "Match in Manchester against Liverpool",
  "Which events are there for Manchester United next week",
];

const prompt = ref("");
const promptElement = useTemplateRef<HTMLInputElement>("promptElement");

const applySuggestion = async (suggestion: string) => {
  prompt.value = t(suggestion);
  await nextTick();
  promptElement.value?.focus();
};

const submit = () => {
  if (!prompt.value.trim()) return;
  emit("submit", prompt.value.trim());
};
</script>
